// v10/src/components/admin/inventory/modals/InventoryItemDetailModal.js
//
// Modal "Ver detalle" del Stock de repuestos (A8) — Tarea 2.
// Tabs: Info, Compatibilidades, S/N (sólo serialized), Movimientos.
//
// Endpoint:
//   GET /api/inventory-items/:id
// (Devuelve item + compatibilities[] + serialized_items[] + recent_movements[])

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Loader, Package, Plane, Hash, ArrowRightLeft,
  Edit3, Plus, AlertCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import inventoryService from '../../../../services/inventoryService';
import { getCategoryIcon } from '../categoryIcons';
import CompatibilityModal from './CompatibilityModal';
import SerializedItemModal from './SerializedItemModal';
import MovementsHistoryModal from './MovementsHistoryModal';

const TAB_INFO = 'info';
const TAB_COMPATS = 'compats';
const TAB_SERIALIZED = 'serialized';
const TAB_MOVEMENTS = 'movements';

const formatStock = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 3, minimumFractionDigits: 0 });
};

const formatDateTime = (value, locale = 'es-AR') => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDate = (value, locale = 'es-AR') => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const InventoryItemDetailModal = ({
  isOpen,
  itemId,
  aircraft = [],
  onClose,
  onUpdated,
  darkMode,
  locale = 'es-AR',
  canEdit = true,
  initialTab = TAB_INFO,
}) => {
  const { t } = useTranslation('admin');

  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Sub-modales internos
  const [showCompats, setShowCompats] = useState(false);
  const [serializedModal, setSerializedModal] = useState(null); // { mode, serializedItem }
  const [showMovementsHistory, setShowMovementsHistory] = useState(false);

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const reload = useCallback(() => {
    if (!itemId) return;
    setLoading(true);
    setError('');
    inventoryService.getById(itemId)
      .then((res) => {
        if (!isMounted.current) return;
        setData(res?.data?.data || null);
      })
      .catch(() => {
        if (!isMounted.current) return;
        setError(t('inventory_modal_detail_load_error'));
      })
      .finally(() => {
        if (isMounted.current) setLoading(false);
      });
  }, [itemId, t]);

  useEffect(() => {
    if (!isOpen || !itemId) return;
    setTab(initialTab);
    reload();
  }, [isOpen, itemId, reload, initialTab]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !showCompats && !serializedModal && !showMovementsHistory) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, showCompats, serializedModal, showMovementsHistory]);

  const isSerialized = data?.tracking_mode === 'serialized';

  const tabs = useMemo(() => {
    const t1 = [
      { id: TAB_INFO, label: t('inventory_modal_detail_tab_info') },
      { id: TAB_COMPATS, label: t('inventory_modal_detail_tab_compatibilities') },
    ];
    if (isSerialized) {
      t1.push({ id: TAB_SERIALIZED, label: t('inventory_modal_detail_tab_serialized') });
    }
    t1.push({ id: TAB_MOVEMENTS, label: t('inventory_modal_detail_tab_movements') });
    return t1;
  }, [isSerialized, t]);

  if (!isOpen || !itemId) return null;

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';

  const Icon = getCategoryIcon(data?.category);
  const categoryLabel = data?.category
    ? t(`inventory_categories_${data.category}`, { defaultValue: data.category })
    : '';
  const stockUnitLabel = data?.stock_unit
    ? t(`inventory_units_${data.stock_unit}`, { defaultValue: data.stock_unit })
    : '';

  const subtitleParts = [];
  if (categoryLabel) subtitleParts.push(categoryLabel);
  if (data?.manufacturer) subtitleParts.push(data.manufacturer);

  // ── Sub-renderers ────────────────────────────────────────────────
  const renderInfoTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 80, height: 80, borderRadius: 18,
          background: data?.is_low_stock ? 'rgba(234,88,12,0.14)' : 'rgba(99,102,241,0.14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, overflow: 'hidden',
        }}>
          {data?.photo_url ? (
            <img src={data.photo_url} alt={data.name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Icon style={{ width: 36, height: 36, color: data?.is_low_stock ? '#ea580c' : '#6366f1' }} />
          )}
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: fgFaint, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {t('inventory_modal_detail_info_stock_label')}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 26, fontWeight: 900, color: fgPrimary, letterSpacing: '-0.4px', lineHeight: 1.05 }}>
            {formatStock(data?.current_stock_quantity, locale)} {stockUnitLabel}
          </p>
          {Number(data?.minimum_stock_quantity) > 0 && (
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: fgMuted }}>
              {t('inventory_modal_detail_info_minimum_label')}: {formatStock(data.minimum_stock_quantity, locale)} {stockUnitLabel}
            </p>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <InfoRow label={t('inventory_modal_detail_info_tracking_label')}
          value={data?.tracking_mode
            ? t(`inventory_modal_create_custom_tracking_${data.tracking_mode}`, { defaultValue: data.tracking_mode })
            : '—'}
          darkMode={darkMode} />
        <InfoRow label={t('inventory_modal_detail_info_stock_unit_label')}
          value={data?.stock_unit
            ? t(`inventory_purchase_units_${data.stock_unit}`, { defaultValue: data.stock_unit })
            : '—'}
          darkMode={darkMode} />
        {data?.purchase_unit && (
          <InfoRow label={t('inventory_modal_detail_info_purchase_unit_label')}
            value={t(`inventory_purchase_units_${data.purchase_unit}`, { defaultValue: data.purchase_unit })}
            darkMode={darkMode} />
        )}
        {data?.units_per_purchase && (
          <InfoRow label={t('inventory_modal_detail_info_units_per_purchase_label')}
            value={String(data.units_per_purchase)} darkMode={darkMode} />
        )}
        {data?.product_code && (
          <InfoRow label={t('inventory_modal_detail_info_product_code_label')}
            value={data.product_code} darkMode={darkMode} />
        )}
      </div>

      {data?.description && (
        <InfoRow label={t('inventory_modal_detail_info_description_label')}
          value={data.description} darkMode={darkMode} block />
      )}
      {data?.notes && (
        <InfoRow label={t('inventory_modal_detail_info_notes_label')}
          value={data.notes} darkMode={darkMode} block />
      )}

      <div style={{
        padding: '10px 14px', borderRadius: 12,
        background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
        border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
      }}>
        <p style={{ margin: 0, fontSize: 11.5, color: fgMuted }}>
          {t('inventory_modal_detail_info_audit_created', {
            user: data?.created_by_name || '—',
            date: formatDateTime(data?.created_at, locale),
          })}
        </p>
        {data?.updated_at && data.updated_at !== data.created_at && (
          <p style={{ margin: '2px 0 0', fontSize: 11.5, color: fgMuted }}>
            {t('inventory_modal_detail_info_audit_updated', {
              user: data?.updated_by_name || '—',
              date: formatDateTime(data?.updated_at, locale),
            })}
          </p>
        )}
      </div>
    </div>
  );

  const renderCompatsTab = () => {
    const compats = Array.isArray(data?.compatibilities) ? data.compatibilities : [];
    const isUniversal = compats.length === 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {isUniversal ? (
          <div style={{
            padding: 16, borderRadius: 14,
            background: darkMode ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.06)',
            border: '1px solid rgba(99,102,241,0.18)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <Plane style={{ width: 20, height: 20, color: '#6366f1' }} />
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: fgPrimary }}>
                {t('inventory_modal_detail_compatibilities_universal_title')}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 12.5, color: fgMuted }}>
                {t('inventory_modal_detail_compatibilities_universal_desc')}
              </p>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {compats.map((c) => (
              <span key={c.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 12px', borderRadius: 999,
                background: darkMode ? 'rgba(99,102,241,0.16)' : 'rgba(99,102,241,0.10)',
                color: '#6366f1', fontSize: 13, fontWeight: 700,
              }}>
                <Plane style={{ width: 13, height: 13 }} />
                {c.aircraft_manufacturer} · {c.aircraft_model_pattern}
              </span>
            ))}
          </div>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={() => setShowCompats(true)}
            style={{
              alignSelf: 'flex-start',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 14px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
              color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
            }}
          >
            <Edit3 style={{ width: 14, height: 14 }} />
            {t('inventory_modal_detail_compatibilities_edit')}
          </button>
        )}
      </div>
    );
  };

  const renderSerializedTab = () => {
    const items = Array.isArray(data?.serialized_items) ? data.serialized_items : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {canEdit && (
          <button
            type="button"
            onClick={() => setSerializedModal({ mode: 'create', serializedItem: null })}
            style={{
              alignSelf: 'flex-start',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 14px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg,#0d9488,#14b8a6)',
              color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
            }}
          >
            <Plus style={{ width: 14, height: 14 }} />
            {t('inventory_modal_detail_serialized_add')}
          </button>
        )}
        {items.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: fgFaint }}>
            {t('inventory_modal_detail_serialized_empty')}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((s) => (
              <button
                key={s.id} type="button"
                onClick={() => canEdit && setSerializedModal({ mode: 'edit', serializedItem: s })}
                disabled={!canEdit}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', borderRadius: 12,
                  background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                  border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
                  cursor: canEdit ? 'pointer' : 'default',
                  textAlign: 'left', fontFamily: 'inherit',
                }}
              >
                <Hash style={{ width: 14, height: 14, color: '#0d9488' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: fgPrimary }}>
                    {s.serial_number}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 11.5, color: fgMuted }}>
                    {s.status === 'installed' && s.aircraft_registration
                      ? `${s.aircraft_registration} · ${formatDate(s.installed_at_date, locale)}${s.installed_at_horometer != null ? ` · ${t('inventory_modal_detail_serialized_horometer_label')} ${s.installed_at_horometer}` : ''}`
                      : s.status === 'retired'
                      ? `${t('inventory_modal_detail_serialized_status_retired')} · ${formatDate(s.retired_at_date, locale)}`
                      : t('inventory_modal_detail_serialized_status_in_stock')}
                  </p>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 800,
                  padding: '3px 9px', borderRadius: 999,
                  background: s.status === 'in_stock'
                    ? 'rgba(99,102,241,0.16)'
                    : s.status === 'installed'
                    ? 'rgba(13,148,136,0.16)'
                    : 'rgba(148,163,184,0.16)',
                  color: s.status === 'in_stock'
                    ? '#6366f1'
                    : s.status === 'installed'
                    ? '#0d9488'
                    : '#64748b',
                  textTransform: 'uppercase', letterSpacing: '0.4px',
                }}>
                  {t(`inventory_modal_detail_serialized_status_${s.status}`)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMovementsTab = () => {
    const movs = Array.isArray(data?.recent_movements) ? data.recent_movements : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {movs.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: fgFaint }}>
            {t(
              data?.tracking_mode === 'serialized'
                ? 'inventory_modal_detail_movements_empty_serialized'
                : 'inventory_modal_detail_movements_empty'
            )}
          </p>
        ) : (
          movs.map((m) => {
            const sign = m.movement_type === 'intake' ? '+' : (m.movement_type === 'dispatch' ? '-' : '');
            const color = m.movement_type === 'intake'
              ? '#16a34a'
              : m.movement_type === 'dispatch'
              ? '#ea580c'
              : '#6366f1';
            const bg = `${color}20`;
            return (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 12,
                background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
              }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 11, fontWeight: 800,
                  padding: '4px 9px', borderRadius: 999,
                  background: bg, color, textTransform: 'uppercase', letterSpacing: '0.4px',
                }}>
                  {t(`inventory_modal_movement_type_${m.movement_type}`)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: fgPrimary }}>
                    {sign}{formatStock(m.quantity, locale)}{stockUnitLabel ? ` ${stockUnitLabel}` : ''}
                    {m.aircraft_registration ? ` → ${m.aircraft_registration}` : ''}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 11.5, color: fgMuted }}>
                    {formatDateTime(m.created_at, locale)}
                    {m.recorded_by_name ? ` · ${m.recorded_by_name}` : ''}
                    {m.reason ? ` · ${m.reason}` : ''}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <button
          type="button"
          onClick={() => setShowMovementsHistory(true)}
          style={{
            margin: '4px 0 0',
            alignSelf: 'flex-end',
            padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
            color: fgPrimary, fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
          }}
        >
          {t('inventory_modal_detail_movements_view_all')}
        </button>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 720, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(79,70,229,0.3)',
            }}>
              <Package style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h3 style={{
                margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {data?.name || ''}
              </h3>
              <p style={{
                margin: '3px 0 0', fontSize: 13, color: fgMuted,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {subtitleParts.join(' · ')}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose}
            aria-label={t('inventory_modal_close')}
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: 'pointer', color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Tabs */}
        <div role="tablist" style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16,
          padding: 4, borderRadius: 12,
          background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
          border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
        }}>
          {tabs.map((tt) => {
            const isSel = tt.id === tab;
            return (
              <button
                key={tt.id} type="button" role="tab"
                aria-selected={isSel}
                onClick={() => setTab(tt.id)}
                style={{
                  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: 'pointer',
                  background: isSel ? (darkMode ? 'rgba(255,255,255,0.10)' : 'white') : 'transparent',
                  color: isSel ? (darkMode ? 'white' : '#0f172a') : fgMuted,
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                  boxShadow: isSel ? '0 2px 8px rgba(15,23,42,0.08)' : 'none',
                }}
              >
                {tt.id === TAB_INFO && <Package style={{ width: 13, height: 13, marginRight: 6, verticalAlign: '-2px' }} />}
                {tt.id === TAB_COMPATS && <Plane style={{ width: 13, height: 13, marginRight: 6, verticalAlign: '-2px' }} />}
                {tt.id === TAB_SERIALIZED && <Hash style={{ width: 13, height: 13, marginRight: 6, verticalAlign: '-2px' }} />}
                {tt.id === TAB_MOVEMENTS && <ArrowRightLeft style={{ width: 13, height: 13, marginRight: 6, verticalAlign: '-2px' }} />}
                {tt.label}
              </button>
            );
          })}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 200 }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: fgMuted, fontSize: 14, padding: '24px 0' }}>
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              <Loader style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} />
              {t('inventory_modal_detail_loading')}
            </div>
          )}
          {error && !loading && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 16px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
              color: '#dc2626', fontSize: 13, fontWeight: 600,
            }}>
              <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
              {error}
            </div>
          )}
          {!loading && !error && data && (
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15 }}
              >
                {tab === TAB_INFO && renderInfoTab()}
                {tab === TAB_COMPATS && renderCompatsTab()}
                {tab === TAB_SERIALIZED && renderSerializedTab()}
                {tab === TAB_MOVEMENTS && renderMovementsTab()}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </motion.div>

      {/* Sub-modales internos */}
      {showCompats && data && (
        <CompatibilityModal
          isOpen={showCompats}
          item={data}
          darkMode={darkMode}
          onClose={() => { setShowCompats(false); reload(); }}
          onSuccess={() => { onUpdated?.(); }}
        />
      )}
      {serializedModal && data && (
        <SerializedItemModal
          isOpen={!!serializedModal}
          mode={serializedModal.mode}
          item={data}
          serializedItem={serializedModal.serializedItem}
          aircraft={aircraft}
          darkMode={darkMode}
          onClose={() => { setSerializedModal(null); reload(); }}
          onSuccess={() => { onUpdated?.(); }}
        />
      )}
      {showMovementsHistory && data && (
        <MovementsHistoryModal
          isOpen={showMovementsHistory}
          itemId={data.id}
          itemName={data.name}
          stockUnitLabel={stockUnitLabel}
          aircraft={aircraft}
          darkMode={darkMode}
          locale={locale}
          onClose={() => setShowMovementsHistory(false)}
        />
      )}
    </motion.div>
  );
};

const InfoRow = ({ label, value, darkMode, block = false }) => {
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 12,
      background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
      gridColumn: block ? '1 / -1' : 'auto',
    }}>
      <p style={{
        margin: 0, fontSize: 11, fontWeight: 700,
        color: fgFaint, textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}
      </p>
      <p style={{
        margin: '4px 0 0', fontSize: 13.5, color: fgPrimary, fontWeight: 600,
        whiteSpace: block ? 'pre-line' : 'normal',
      }}>
        {value || '—'}
      </p>
    </div>
  );
};

export default InventoryItemDetailModal;
